import { Button, useTranslation } from '@hbs-us/ui';
import { useState } from 'react';

import { useAccess } from '../../auth/useAccess.js';
import { ApiError } from '../../lib/apiFetch.js';
import type { TimesheetActiveClock } from './contractsMirror.js';
import { useAdminEditOpenEntryMutation, useResolveOpenMutation } from './useApprovalMutation.js';
import { elapsedMinutesSince, useNow } from './useNow.js';

/**
 * "Clocked in now" — who is on the clock across the tenant, right now.
 *
 * The data rides on the week-grid response (`activeClocks`), which refetches every 30s, and the
 * elapsed timers tick client-side every second in between. So the panel reads as live without a
 * second endpoint and without polling harder than the information actually changes.
 *
 * It carries the two corrections a supervisor needs on a shift that has not ended: move the start
 * time (someone was started wrong, or backstamped wrong) and close it out (`resolve-open`). Both
 * re-check on the server; hiding a control here is UX, never authorization.
 */

/**
 * How long a shift runs before the panel calls it out.
 *
 * SIXTEEN hours, not twelve. Crews here work past twelve consistently enough that a twelve-hour
 * flag would fire on ordinary days, and a flag that fires on ordinary days is one people learn to
 * ignore — at which point the real forgotten clock-out sails past with it.
 *
 * Worth surfacing at all because a shift left open does not merely look untidy: approveWeekPre-
 * conditionError refuses to approve a week holding ANY open entry, so a missed clock-out resurfaces
 * days later as "why can I not approve this week", a long way from the screen that could have said.
 */
const LONG_SHIFT_MINUTES = 16 * 60;

/** The field treatment this screen already uses, so the panel reads as part of Timesheets. */
const CONTROL =
  'mt-1 h-10 w-full min-w-0 rounded-md border border-border bg-background px-3 text-foreground text-sm ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-background';

export function OnClockRoster({ clocks }: { clocks: TimesheetActiveClock[] }) {
  const { t } = useTranslation();
  if (clocks.length === 0) return null;

  return (
    <section
      aria-label={t('timesheets:activeClocks.regionLabel')}
      className="mb-4 rounded-md border border-border bg-card p-4"
    >
      <h2 className="mb-2 font-semibold text-foreground text-sm">
        {t('timesheets:activeClocks.heading')}
      </h2>
      <ul className="flex flex-col gap-2">
        {clocks.map((clock) => (
          <ActiveClockRow key={clock.id} clock={clock} />
        ))}
      </ul>
    </section>
  );
}

/**
 * One active (open) clock entry (Plan 22-24). Beyond the display chip, it offers a "Resolve open
 * entry" action: expand an inline clock-out-time input → POST /api/v1/timesheets/:id/resolve-open.
 * The SERVER recomputes the lunch-adjusted hours + labor cost; a guard rejection surfaces inline.
 */
function ActiveClockRow({ clock }: { clock: TimesheetActiveClock }) {
  const { t } = useTranslation();
  const resolveMutation = useResolveOpenMutation();
  // `POST /timesheets/:id/resolve-open` allows the entry's OWN employee or anyone with
  // `time.approve`. This list shows the whole crew's open clocks, and the session does
  // not carry an employeeId to match against, so the action is offered to approvers
  // only. A worker closing their own forgotten shift still does it from MyClock.
  const canApprove = useAccess()({ permission: 'time.approve' });
  const [open, setOpen] = useState(false);
  const [clockOutLocal, setClockOutLocal] = useState('');

  const editMutation = useAdminEditOpenEntryMutation();
  const [editing, setEditing] = useState(false);
  const [startLocal, setStartLocal] = useState('');
  const [startReason, setStartReason] = useState('');

  // Ticks every second so the panel reads as live between the 30s refetches, without asking the
  // server anything. The figure is STATUS: the server owns what this shift eventually pays.
  const now = useNow();
  const elapsed = elapsedMinutesSince(clock.clockInAt, now);
  const elapsedLabel =
    elapsed === null
      ? null
      : elapsed >= 60
        ? t('timesheets:myClock.elapsedHm', {
            hours: Math.floor(elapsed / 60),
            minutes: elapsed % 60,
          })
        : t('timesheets:myClock.elapsedM', { minutes: elapsed });
  const overLong = elapsed !== null && elapsed >= LONG_SHIFT_MINUTES;

  const errorMessage =
    resolveMutation.error instanceof ApiError
      ? t(`timesheets:resolveOpen.errors.${resolveMutation.error.message}`, {
          defaultValue: t('timesheets:resolveOpen.errors.generic'),
        })
      : null;

  const editError =
    editMutation.error instanceof ApiError
      ? t(`timesheets:activeClocks.errors.${editMutation.error.message}`, {
          defaultValue: t('timesheets:activeClocks.errors.generic'),
        })
      : null;

  function submitStart(event: React.FormEvent) {
    event.preventDefault();
    if (!startLocal || !startReason.trim()) return;
    editMutation.mutate(
      {
        entryId: clock.id,
        // datetime-local is wall-clock; the server wants an instant.
        clockInAt: new Date(startLocal).toISOString(),
        editReason: startReason.trim(),
      },
      {
        onSuccess: () => {
          setEditing(false);
          setStartLocal('');
          setStartReason('');
        },
      },
    );
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!clockOutLocal) return;
    // datetime-local is wall-clock; convert to an ISO instant for the server.
    const iso = new Date(clockOutLocal).toISOString();
    resolveMutation.mutate(
      { entryId: clock.id, clockOutAt: iso },
      {
        onSuccess: () => {
          setOpen(false);
          setClockOutLocal('');
        },
      },
    );
  }

  return (
    <li className="rounded-md bg-secondary/40 px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="size-2 rounded-full bg-primary" aria-hidden="true" />
        <span className="font-medium text-foreground">{clock.employeeName}</span>
        <span className="text-muted-foreground">· {clock.jobName}</span>
        {elapsed !== null ? (
          <span className="font-mono text-muted-foreground text-xs">{elapsedLabel}</span>
        ) : null}
        {overLong ? (
          // TEXT, not colour alone (A11Y-01). A shift past sixteen hours is nearly always a
          // forgotten clock-out, and it is worth surfacing HERE because it silently blocks week
          // approval later — approveWeekPreconditionError refuses a week holding any open entry,
          // and at that point the cause is a week away from the screen showing it.
          <span className="rounded bg-destructive/10 px-1.5 py-0.5 font-medium text-destructive text-xs">
            {t('timesheets:activeClocks.probablyForgotten')}
          </span>
        ) : null}
        {canApprove ? (
          <div className="ml-auto flex flex-wrap gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setEditing((prev) => !prev);
                setOpen(false);
              }}
            >
              {t('timesheets:activeClocks.editStart')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setOpen((prev) => !prev);
                setEditing(false);
              }}
            >
              {t('timesheets:resolveOpen.action')}
            </Button>
          </div>
        ) : null}
      </div>

      {editing ? (
        <form className="mt-3 flex flex-wrap items-end gap-2" onSubmit={submitStart}>
          <div>
            <label htmlFor={`start-${clock.id}`} className="font-medium text-foreground text-xs">
              {t('timesheets:activeClocks.startLabel')}
            </label>
            <input
              id={`start-${clock.id}`}
              type="datetime-local"
              className={CONTROL}
              value={startLocal}
              onChange={(e) => setStartLocal(e.target.value)}
              required
            />
          </div>
          <div className="min-w-0 flex-1">
            <label htmlFor={`start-why-${clock.id}`} className="font-medium text-foreground text-xs">
              {t('timesheets:activeClocks.reasonLabel')}
            </label>
            <input
              id={`start-why-${clock.id}`}
              type="text"
              className={CONTROL}
              value={startReason}
              maxLength={500}
              onChange={(e) => setStartReason(e.target.value)}
              required
            />
          </div>
          <Button
            type="submit"
            size="sm"
            disabled={editMutation.isPending || !startLocal || !startReason.trim()}
          >
            {t('timesheets:activeClocks.saveStart')}
          </Button>
          <Button type="button" variant="secondary" size="sm" onClick={() => setEditing(false)}>
            {t('timesheets:resolveOpen.cancel')}
          </Button>
          {editError ? (
            <p role="alert" className="w-full text-destructive text-xs">
              {editError}
            </p>
          ) : null}
        </form>
      ) : null}

      {open ? (
        <form className="mt-3 flex flex-wrap items-end gap-2" onSubmit={submit}>
          <div>
            <label htmlFor={`resolve-${clock.id}`} className="font-medium text-foreground text-xs">
              {t('timesheets:resolveOpen.clockOutLabel')}
            </label>
            <input
              id={`resolve-${clock.id}`}
              type="datetime-local"
              className="mt-1 h-10 rounded-md border border-border bg-background px-3 text-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              value={clockOutLocal}
              onChange={(e) => setClockOutLocal(e.target.value)}
              required
            />
          </div>
          <Button type="submit" size="sm" disabled={resolveMutation.isPending || !clockOutLocal}>
            {t('timesheets:resolveOpen.submit')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              resolveMutation.reset();
              setOpen(false);
            }}
          >
            {t('timesheets:resolveOpen.cancel')}
          </Button>
          {errorMessage ? (
            <p role="alert" aria-live="assertive" className="w-full text-destructive text-sm">
              {errorMessage}
            </p>
          ) : null}
        </form>
      ) : null}
    </li>
  );
}
